<template>
  <b-row>
    <b-col cols="12" class="pr-0 pl-0">
      <div class="h6 mt-1 mb-0" >
        <i class="far fa-calendar-alt fa-lg mr-2 ml-2"></i> {{ $t('menu.timetable.show') }}
      </div>
      <hr class="mt-2 mb-2">
        <full-calendar :options="calendarOptions" ref="fullCalendar"></full-calendar>
    </b-col>
    <show-relations-modal
      v-if="relationIRI !== null"
      :show-modal="isRelationsModalVisible"
      :iri="relationIRI"
      @hideModal="hideRelationsModal()">
    </show-relations-modal>
  </b-row>
</template>

<script>
import { mapGetters } from 'vuex'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import timelinePlugin from '@fullcalendar/timeline'
import interactionPlugin from '@fullcalendar/interaction'
import pl from '@fullcalendar/core/locales/pl'
import getId from '@/utils/getIdFromLink'
import ApiService from '@/utils/ApiService'
import ShowRelationsModal from '@/components/Dashboard/Views/common/ShowRelationsModal'
import { checkPermission } from '@/utils/checkRoles'

export default {
  name: 'Calendar',
  components: {
    ShowRelationsModal,
    FullCalendar
  },
  props: {
    idUserData: {
      required: true
    }
  },
  computed: {
    ...mapGetters({
      relationCreated: 'excmrelation/create/created'
    }),
    ableToViewDailyPlan () {
      return checkPermission('servicemen', 'DailyPlanShow')
    }
  },
  data () {
    return {
      isRelationsModalVisible: false,
      relationIRI: null,
      isLoading: true,
      firstInit: true,
      relations: [],
      dailyPlanItems: [],
      dateFrom: this.$moment().startOf('month').format('YYYY-MM-DD'),
      dateTo: this.$moment().endOf('month').format('YYYY-MM-DD'),
      calendarOptions: {
        schedulerLicenseKey: '0826492648-fcs-1630368000',
        themeSystem: 'standard',
        eventTimeFormat: { // like '14:30:00'
          hour: '2-digit',
          minute: '2-digit'
        },
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        },
        contentHeight: 500,
        locale: pl,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          listPlugin,
          interactionPlugin,
          timelinePlugin
        ],
        initialView: 'dayGridMonth',
        dateClick: this.handleDateClick,
        eventClick: this.handleEventClick,
        events: [],
        datesSet: (dateInfo) => {
          if (this.dateFrom !== this.$moment(dateInfo.start).format('YYYY-MM-DD') || this.dateTo !== this.$moment(dateInfo.end).format('YYYY-MM-DD')) {
            this.dateFrom = this.$moment(dateInfo.start).format('YYYY-MM-DD')
            this.dateTo = this.$moment(dateInfo.end).format('YYYY-MM-DD')
            this.reload()
          }
        }
      }
    }
  },
  watch: {
    idUserData () {
      this.reload()
    },
    relationCreated () {
      this.reload()
    }
  },
  created () {
    this.channelTasks = this.$store.state.Pusher.subscribe('e_x_c_m_tasks')
    this.channelTasks.bind('e_x_c_m_tasks', data => {
      this.reload()
    })
    this.channelDailyPlanItems = this.$store.state.Pusher.subscribe('e_x_c_m_daily_plan_items')
    this.channelDailyPlanItems.bind('e_x_c_m_daily_plan_items', data => {
      this.reload()
    })
  },
  destroyed () {
    this.$store.state.Pusher.unsubscribe('e_x_c_m_tasks')
    this.$store.state.Pusher.unsubscribe('e_x_c_m_daily_plan_items')
  },
  mounted () {
    // this.getRelations()
    // this.getDailyPlanItems()
  },
  methods: {
    reload () {
      this.calendarOptions.events = []
      this.getRelations()
      this.getDailyPlanItems()
    },
    async handleEventClick (calendarObject) {
      let eventId = calendarObject.event._def.publicId
      if (eventId.includes('e_x_c_m_tasks')) {
        let decision = await this.promptUser(this.$t('global.goToTask'))
        if (decision) {
          this.$router.push({ name: 'EXCMTaskShow', params: { id: eventId } })
        }
      } else if (eventId.includes('e_x_c_m_relations')) {
        this.isRelationsModalVisible = true
        this.relationIRI = eventId
      }
    },
    async handleDateClick (date) {
      if (!this.ableToViewDailyPlan) return
      let decision = await this.promptUser(this.$t('global.goToDailyPlan'))
      if (decision) {
        this.$router.push({
          name: 'DailyPlanShow',
          params: {
            idServiceman: this.idUserData,
            date: date.dateStr
          }
        })
      }
    },
    promptUser (title) {
      return this.$Swal({
        title: title,
        showCancelButton: true,
        confirmButtonText: this.$t('global.yes'),
        cancelButtonText: this.$t('global.no')
      })
    },
    hideRelationsModal () {
      this.isRelationsModalVisible = false
      this.relationIRI = null
    },
    async getRelations () {
      let params = {
        'relationUserData.userData': this.idUserData,
        'state': 1,
        'pagination': false
      }
      let url = 'e_x_c_m_relations'
      const response = await new ApiService(url, params).getResults()
      this.relations = []
      let data = response.items
      for (let i in data) {
        let element = {}
        element.id = data[i]['@id']
        element.title = data[i].title
        element.start = this.$moment(data[i].dateFrom).format('YYYY-MM-DD HH:mm')
        element.end = this.$moment(data[i].dateTo).format('YYYY-MM-DD HH:mm')
        this.calendarOptions.events.push(element)
      }
      this.isLoading = false
      let calendarApi = this.$refs.fullCalendar.getApi()
      calendarApi.render()
    },
    async getDailyPlanItems () {
      let params = {
        'idUserData': this.idUserData,
        'pagination': false,
        'date[after]': this.dateFrom,
        'date[before]': this.dateTo
      }
      const response = await new ApiService('e_x_c_m_daily_plan_items', params).getResults()
      let data = response.items
      for (let i in data) {
        let element = {}
        let taskType = data[i].task.idTaskType.title
        let servicemanName = data[i].idUserData.name + ' ' + data[i].idUserData.surname
        let contractorName = ''
        if (data[i].task.idCase.contractorUserData) {
          contractorName += data[i].task.idCase.contractorUserData.firmname1
        }
        let status = ''
        let caseNumberText = data[i].task.idCase.number ? data[i].task.idCase.number : this.getIdLink(data[i].task.idCase['@id'])
        if (data[i].task.finished === 1) { // Zakończone zadania nie pojawią się na kalendarzu
          continue
        } else if (data[i].status === 1 && !(data[i].dateStart) && !(data[i].dateStop)) {
          status = this.$t('views.excmtask.show.dailyPlanItemStatuses.notOver')
        } else if (data[i].status === 1 && data[i].dateStart && !(data[i].dateStop)) {
          status = this.$t('global.processing')
        } else if (data[i].status === 1 && data[i].dateStart && data[i].dateStop) {
          status = this.$t('global.over')
        } else if (data[i].status === 0 && data[i].dateStart && data[i].dateStop) {
          status = this.$t('global.held')
        } else if ((data[i].status === 2) && data[i].dateStart) {
          continue
        } else if ((data[i].status === 2) && !(data[i].dateStart)) {
          continue
        } else if ((data[i].status === 3) && data[i].dateStart) {
          status = this.$t('global.movedToMainStarted')
        } else if ((data[i].status === 3) && !(data[i].dateStart)) {
          status = this.$t('global.movedToMainNotStarted')
        }
        element.title = '\n' + caseNumberText + ' /' + '\n' + taskType + '\n' + status + '\n' + servicemanName + '\n' + contractorName
        if (data[i].dateStart) {
          element.start = this.$moment(data[i].dateStart).format('YYYY-MM-DD HH:mm:ss')
        } else {
          element.start = this.$moment(data[i].date).format('YYYY-MM-DD')
        }
        if (data[i].dateStop) {
          element.end = this.$moment(data[i].dateStop).format('YYYY-MM-DD HH:mm:ss')
        } else {
          element.end = this.$moment(data[i].date).format('YYYY-MM-DD')
        }
        if (!data[i].dateStart && !data[i].dateStop && data[i].timeStartOnCalendar) {
          element.start = this.$moment(data[i].date).format('YYYY-MM-DD') + ' ' + this.$moment(data[i].timeStartOnCalendar).format('HH:mm:ss')
          // 60 minut później
          element.end = this.$moment(element.start).add(60, 'minutes').format('YYYY-MM-DD HH:mm:ss')
        }
        if (data[i].task.idTaskType.color) {
          element.color = data[i].task.idTaskType.color
          element.textColor = '#000000'
        } else if (data[i].status === 0) {
          element.backgroundColor = this.$store.getters['configuration/cancelledDailyPlanItemColor']
          element.textColor = '#000000'
        } else if (data[i].status === 1) {
          element.backgroundColor = this.$store.getters['configuration/activeDailyPlanItemColor']
          element.textColor = '#000000'
        } else if (data[i].status === 2) {
          element.backgroundColor = this.$store.getters['configuration/movedDailyPlanItemColor']
          element.textColor = '#000000'
        } else if (data[i].status === 3) {
          element.backgroundColor = this.$store.getters['configuration/returnedDailyPlanItemColor']
          element.textColor = '#000000'
        }
        element.id = data[i].task['@id']
        element.customOrdering = data[i].customOrdering
        this.calendarOptions.events.push(element)
      }
      this.isLoading = false
      let calendarApi = this.$refs.fullCalendar.getApi()
      calendarApi.render()
    },
    getIdLink (link) {
      return getId(link)
    }
  }
}
</script>

<style>
  h2 {
    font-size: 1rem !important;
  }
  .fc th {
    font-size: 0.9em !important;
  }
  .fc-toolbar {
    margin-bottom: 5px !important;
  }
  .fc button {
    padding: 0.25rem 0.35rem !important;
  }
</style>
